import type { FC } from 'preact/compat';

import { Icon16Done, Icon24CheckCircleOn, Icon24ChevronDown } from '@vkontakte/icons';

import { usePhoneContext } from '../../context/PhoneContext';
import { useWidget } from '../../context/WidgetContext';
import { useInputPhone } from './lib/hooks';
import { renderCountryList } from './lib/utils';

import { PhonePatternInput } from './PhonePatternInput';

import styles from './styles.module.css';

interface Props {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export const InputPhone: FC<Props> = ({ value, onChange, error, disabled = false }) => {
  const { selectedCountry: contextCountry, setSelectedCountry } = usePhoneContext();
  const { localeMessages, locale } = useWidget();
  const {
    selectedCountry,
    isSelectOpen,
    validationError,
    panelTransform,
    backdropStyle,
    panelClassName,
    inputCombinedClassName,
    chevronClassName,
    getOptionClassName,
    wrapperRef,
    panelRef,
    handleCountryChange,
    handleValueChange,
    handleRemoveFormatting,
    handleSelectClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useInputPhone({
    value,
    onChange,
    error,
    disabled,
    selectedCountry: contextCountry,
    setSelectedCountry,
    phoneMessages: localeMessages.phone,
  });

  return (
    <div className={styles.inputPhoneContainer} ref={wrapperRef}>
      <div className={styles.phoneInputWrapper}>
        <div className={inputCombinedClassName}>
          <div className={styles.countryCodeDisplay} onClick={handleSelectClick}>
            {selectedCountry.dialCode}
            <Icon24ChevronDown className={chevronClassName} fill="var(--vkui--color_icon_secondary)" />
          </div>

          <PhonePatternInput
            key={selectedCountry.code}
            country={selectedCountry}
            value={value}
            onValueChange={handleValueChange}
            removeFormatting={handleRemoveFormatting}
            disabled={disabled}
            className={styles.phoneInput}
          />
        </div>

        {isSelectOpen && (
          <>
            <div className={styles.backdrop} style={backdropStyle} onClick={handleSelectClick} />

            <div className={styles.countrySelectDropdown}>
              {renderCountryList({
                locale,
                selectedCountry,
                handleCountryChange,
                getClassName: getOptionClassName,
                SelectedIcon: Icon16Done,
              })}
            </div>

            <div
              className={panelClassName}
              ref={panelRef}
              style={panelTransform}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={styles.countrySelectHandle} onTouchStart={handleTouchStart} />
              <div className={styles.countrySelectHeader}>{localeMessages.phone.selectCountryCode}</div>
              <div>
                {renderCountryList({
                  locale,
                  selectedCountry,
                  handleCountryChange,
                  getClassName: styles.mobileCountryOption,
                  SelectedIcon: Icon24CheckCircleOn,
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {(error || validationError) && <div className={styles.errorMessage}>{error || validationError}</div>}
    </div>
  );
};
